import { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Collapse, Modal, Row } from "react-bootstrap";
import { ActiveRosterContext } from "../../Contexts/ActiveRosterProvider";
import { useNavigate } from "react-router";
import { AuthContext } from "../../Auth/AuthProvider";
import Resident from "../../Residents/Models/Resident";
import { abpnClient } from "../../Services/AbpnClient";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";

interface IProps {
    residency: Resident;
    userProgramTypeId: number;
}

export default function ProgramRemovalGrid(props: IProps) {
    const [isConfirmingProgramRemoval, setIsConfirmingProgramRemoval] = useState<boolean>(false);
    const [showProgramRemovalSection, setShowProgramRemovalSection] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [showInvalidAlert, setShowInvalidAlert] = useState<boolean>(false);
    const [showPgyWithMissingDocumentationError, setShowPgyWithMissingDocumentationError] = useState<boolean>(false);
    const [showUnattestedPGYError, setShowUnattestedPGYError] = useState<boolean>(false);
    const [showZeroMonthsError, setShowZeroMonthsError] = useState<boolean>(false);
    const [showUnattestedCSEError, setShowUnattestedCSEError] = useState<boolean>(false);
    const navigate = useNavigate(); 
    const { reloadRoster } = useContext(ActiveRosterContext);

    useEffect(() => {
        const resetGrid = () => {
            validateProgramRemoval();
            setIsConfirmingProgramRemoval(false);
            setShowProgramRemovalSection(false);
        }
        resetGrid();
    }, [props.residency]);

    const authContext = useContext(AuthContext);

    const removeFromProgram = async () => {
        setIsSaving(true);
        
        let url = `/api/training/removeResidentFromProgram?physicianId=${props.residency.physicianId}&programTypeId=${props.userProgramTypeId}`;
        await abpnClient.post<Resident>(url, null, authContext);
        setIsSaving(false);
        navigate(`/`);
        reloadRoster();
    }

    const validateProgramRemoval = () => { 
        let pgyWithMissingDocumentation = props.residency.postGraduateYears.filter(x=>x.trainingId != null && x.trainingDocuments.length == 0)?.map(x=>x.trainingDocuments)?.length;
        let unattestedPGY = props.residency.postGraduateYears.find(_ => _.isAttestedComplete == false);
        let zeroMonthPGYs = props.residency.postGraduateYears.find(_ => _.months == null || _.months == 0);
        let unattestedCSE = props.residency.clinicalSkills.find(_ => _.isAttestedComplete == false);
        let showInvalidAlert = pgyWithMissingDocumentation > 0 || unattestedCSE != null || unattestedPGY != null || zeroMonthPGYs != null;

        setShowInvalidAlert(showInvalidAlert);
        setShowPgyWithMissingDocumentationError(pgyWithMissingDocumentation > 0);
        setShowUnattestedPGYError(unattestedPGY != null);
        setShowZeroMonthsError(zeroMonthPGYs != null);
        setShowUnattestedCSEError(unattestedCSE != null);
    }

    const displayErrorMessages = () => {
        if(showInvalidAlert)
        {
            let errorMessages : string[] = [];
            if(showPgyWithMissingDocumentationError)
                errorMessages.push("For each PGY entered, you must upload documentation of training completed in that PGY before you can remove a resident.");
            if(showUnattestedPGYError)
                errorMessages.push("For each PGY entered, the PD must attest to the completion of the PGY before you can remove a resident.");
            if(showZeroMonthsError)
                errorMessages.push("For each PGY entered, you must enter the number of months/blocks completed in your program before you can remove a resident.");
            if(showUnattestedCSEError)
                errorMessages.push("For each CSE entered, the PD must attest to the completion of the CSE before you can remove a resident.");
        
            return (
                <ul>
                    {errorMessages.map((error) => {
                        return <li>{error}</li>;
                    })}
                </ul>
            );
        }
        else
            return <div></div>;
    }


    return (
        <>
            <div className="d-flex justify-content-between">
                <h5 className="fw-bolder ml-2 mt-1 mb-4 text-primary">Remove Resident from Program</h5>
                <div>
                    <Button className="pc-button me-2" variant="primary" size="sm" hidden={showProgramRemovalSection} onClick={() => setShowProgramRemovalSection(true)}>Begin Program Removal</Button>
                </div>
            </div>
            {showProgramRemovalSection && 
            <Collapse in={showProgramRemovalSection}>
                <>
                    {showInvalidAlert && 
                        <Alert variant={"danger"} dismissible={false}>
                            <Alert.Heading>Resident Cannot Be Removed From Program</Alert.Heading>
                            {displayErrorMessages()}
                        </Alert>
                    }
                    <Row>
                        <Col>
                            <div className="mb-2">
                                Removing a resident from your program's active roster should be used when a resident is transferring out of your program and into another. When the resident is added to your alumni roster, the next program
                                will be able to search and find the resident in order to transfer them into their program. The remove from program action can also be used when a resident makes the decision not to continue their residency.
                            </div>
                            <div className="mb-2">
                                {`Documentation (in the form of a PDF < 5MB) of the training that was completed before removal must be uploaded to the respective PGY and each must be attested to/by the PD in order for the removal action to successfully complete.`}
                            </div>
                        </Col>
                    </Row>
                    <div className="mt-3 mb-3">
                        <strong>Remove Dr. {props.residency.name.lastName} with incomplete training from your program?</strong>
                    </div>
                    <Button className="pc-button me-4" variant="primary" size="sm" disabled={showInvalidAlert} onClick={() => setIsConfirmingProgramRemoval(true)}>Yes, Remove</Button>
                    <Button className="pc-button" variant="primary" size="sm" onClick={() => setShowProgramRemovalSection(false)}>No, Cancel</Button>
                </>
            </Collapse>
            
            }
            <Modal show={isConfirmingProgramRemoval} onHide={()=> setIsConfirmingProgramRemoval(false)}>
                <Modal.Body>
                    <div className="mb-2">
                        Removing a resident from your program with <strong>incomplete training</strong> will remove them from your active roster and add them to your alumni roster. All of the resident 
                        details displayed on this page will be accessible, but uneditable, from the Alumni roster.
                    </div>
                    <div className="mb-2">
                        The Alumni Resident Details for Dr. {props.residency.name.lastName} will display with a status of 'Alumni - Program not completed'.
                    </div>
                    <strong>Are you sure you want to remove Dr. {props.residency.name.lastName} with incomplete training from your training program?</strong>
                </Modal.Body>
                <Modal.Footer>
                    {isSaving ? <AbpnSpinner /> : 
                    <>
                        <Button variant="primary" className="me-4" onClick={() => removeFromProgram()}>
                            Yes, Confirm
                        </Button>
                        <Button variant="outline-secondary" onClick={() => {setIsConfirmingProgramRemoval(false); setShowProgramRemovalSection(false);}}>
                            No, Cancel
                        </Button>
                    </>
                    }
                </Modal.Footer>
            </Modal>
        </>
    );
}